import {
  Banner,
  Box,
  Button,
  Container,
  MuiTable,
  Spacer,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Text,
  TextField,
  Title,
} from '@color/continuum';
import { format, isValid, parseISO } from 'date-fns';
import { FormEvent, useState } from 'react';
import { useSearchParams } from 'react-router-dom-v5-compat';

import { FixedLoadingIndicator } from '../common/compoments/FixedLoadingIndicator';

import { SampleSearchHit, SampleSearchParams, useSampleSearchQuery } from './api';

// `apiKey` is what the backend expects; `urlKey` is what we put in the page URL.
// They're kept short and distinct so they stay readable in shareable links and
// don't collide with params the global `usePersistedQueryCookies` in AppPortals
// moves into cookies and strips from the URL (e.g. `sample_id`).
const SEARCH_FIELDS = [
  { apiKey: 'batch_label', urlKey: 'batch', label: 'Sequencing Batch' },
  { apiKey: 'ssr_barcode', urlKey: 'sample', label: 'Sample Sequence Run' },
  { apiKey: 'kit_barcode', urlKey: 'kit', label: 'Kit Barcode' },
] as const;

type SearchFieldKey = (typeof SEARCH_FIELDS)[number]['apiKey'];

type SearchInputs = Record<SearchFieldKey, string>;

// Build the API params from the raw inputs, dropping blanks so we only send the
// fields the user actually filled in. Returns null when nothing is filled in,
// which keeps the query disabled (the backend requires at least one field).
const toSearchParams = (inputs: SearchInputs): SampleSearchParams | null => {
  const params: SampleSearchParams = {};
  SEARCH_FIELDS.forEach(({ apiKey }) => {
    const value = inputs[apiKey].trim();
    if (value) {
      params[apiKey] = value;
    }
  });
  return Object.keys(params).length > 0 ? params : null;
};

const readInputsFromParams = (params: URLSearchParams): SearchInputs =>
  Object.fromEntries(
    SEARCH_FIELDS.map(({ apiKey, urlKey }) => [apiKey, params.get(urlKey) ?? ''])
  ) as SearchInputs;

const formatScore = (value?: number) => (value == null ? '' : value.toFixed(3));

const formatSequencedAt = (value?: string | null) => {
  if (!value) {
    return '';
  }
  const parsed = parseISO(value);
  return isValid(parsed) ? format(parsed, 'MM/dd/yyyy') : value;
};

export const View = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [inputs, setInputs] = useState<SearchInputs>(() => readInputsFromParams(searchParams));

  // Initialized from the URL so a shared link runs its search on load. Only the
  // submitted params (not the live inputs) drive the query. Searching happened on
  // submit rather than on every keystroke.
  const [submittedParams, setSubmittedParams] = useState<SampleSearchParams | null>(() =>
    toSearchParams(inputs)
  );

  const { data, isFetching, error } = useSampleSearchQuery(submittedParams);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const params = toSearchParams(inputs);
    setSubmittedParams(params);
    setSearchParams(() => {
      const next = new URLSearchParams();
      SEARCH_FIELDS.forEach(({ apiKey, urlKey }) => {
        const value = inputs[apiKey].trim();
        if (value) {
          next.set(urlKey, value);
        }
      });
      return next;
    });
  };

  const canSearch = toSearchParams(inputs) !== null;
  const hits = data?.hits.hits ?? [];

  return (
    <Container maxWidth="xl">
      <Title>Sample Search</Title>
      <Spacer variant="small" />
      <Box
        component="form"
        onSubmit={onSubmit}
        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
      >
        {SEARCH_FIELDS.map(({ apiKey, label }) => (
          <TextField
            key={apiKey}
            label={label}
            value={inputs[apiKey]}
            onChange={({ target: { value } }) =>
              setInputs((prev) => ({ ...prev, [apiKey]: value }))
            }
            optionalIndicatorIsEnabled={false}
            size="small"
            sx={{ flex: 1, maxWidth: 320 }}
          />
        ))}
        <Button type="submit" disabled={!canSearch || isFetching} size="small">
          Search
        </Button>
      </Box>
      <Spacer variant="small" />
      {isFetching && <FixedLoadingIndicator />}
      {!isFetching && error && (
        <Banner severity="error" variant="in-component">
          {error.message}
        </Banner>
      )}
      {!isFetching && !error && data && (
        <>
          <Text variant="body2" color="textSecondary">
            Ranked {data.hits.total} samples in {data.took / 1000} seconds.
          </Text>
          <Spacer variant="small" />
          <TableContainer>
            <MuiTable size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Score</TableCell>
                  <TableCell>Norm Score</TableCell>
                  <TableCell>Batch ID</TableCell>
                  <TableCell>Sample ID</TableCell>
                  <TableCell>Kit Barcode</TableCell>
                  <TableCell>Sequenced At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {hits.map((hit: SampleSearchHit) => (
                  <TableRow key={hit._id}>
                    <TableCell>{formatScore(hit.score)}</TableCell>
                    <TableCell>{formatScore(hit.norm_score)}</TableCell>
                    <TableCell>{hit.batch_id}</TableCell>
                    <TableCell>{hit.sample_id}</TableCell>
                    <TableCell>{hit.kit_barcode}</TableCell>
                    <TableCell>{formatSequencedAt(hit.sequenced_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </MuiTable>
          </TableContainer>
        </>
      )}
    </Container>
  );
};
